import { useEffect, useMemo, useState } from 'react';
import { getCoreRowModel, useReactTable, VisibilityState } from '@tanstack/react-table';
import { ChevronDownIcon, Loader2Icon } from 'lucide-react';

import { CALL_COLUMNS } from '@/app/(app)/calls/components/columns';
import { useCallFiltersCtx } from '@/app/(app)/calls/components/provider';
import { MultiSelect } from '@/components/atoms/multi-select';
import { Popover } from '@/components/atoms/popover';
import { CallDialogContent } from '@/components/call/call-dialog';
import { ColumnVisibilityPicker, DataTable } from '@/components/data-table';
import DateRange from '@/components/daterange';
import { useFeatureFlag } from '@/components/providers/feature-flag-provider';
import { ApiSchemas } from '@/components/providers/v1-api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Sheet } from '@/components/ui/sheet';
import { CALL_HUMAN_REQUEST, CALL_INTENTS, CALL_OUTCOMES } from '@/consts/calls';
import { cn } from '@/lib/utils';

type CallData = ApiSchemas['CallRead'];

interface CallTableProps {
  data: CallData[];
  isLoading: boolean;
}

export function CallTable({ data, isLoading }: CallTableProps) {
  const isOutcomeEnabled = useFeatureFlag('/voice-agent/calls/outcome');
  const [openCallData, setOpenCallData] = useState<CallData | null>(null);
  const [columnVisibility, setColumnVisibility] = useState<VisibilityState>({
    recording_url: false,
  });
  const columns = useMemo(() => {
    if (isOutcomeEnabled) {
      return CALL_COLUMNS;
    }

    return CALL_COLUMNS.filter((column) => column.header !== 'Outcome');
  }, [isOutcomeEnabled]);

  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
    onColumnVisibilityChange: setColumnVisibility,
    state: { columnVisibility },
  });

  return (
    <div className='space-y-3'>
      <div className='flex w-full items-center justify-between gap-2'>
        <TableFilters />
        <div className='flex items-center gap-2'>
          {isLoading && <Loader2Icon className='animate-spin text-muted-foreground' />}
          <ColumnVisibilityPicker table={table} />
        </div>
      </div>
      <div className={cn('overflow-hidden rounded-md border', isLoading && 'animate-pulse')}>
        <DataTable
          table={table}
          columns={CALL_COLUMNS}
          onRowClick={(row) => {
            if (row.original.id === openCallData?.id) {
              setOpenCallData(null);
            } else {
              setOpenCallData(row.original);
            }
          }}
          getIsRowOpen={(row) => row.original.id === openCallData?.id}
        />
      </div>
      <Sheet
        open={!!openCallData}
        onOpenChange={(v) => {
          if (!v) setOpenCallData(null);
        }}
        modal={false}
      >
        {openCallData && <CallDialogContent data={openCallData} />}
      </Sheet>
    </div>
  );
}

function TableFilters() {
  const isOutcomeEnabled = useFeatureFlag('/voice-agent/calls/outcome');
  const { filters, setFilters } = useCallFiltersCtx();
  const [phoneValue, setPhoneValue] = useState(filters.from_number);
  useEffect(() => {
    setPhoneValue(filters.from_number);
  }, [filters.from_number]);

  return (
    <div className='flex items-center gap-2'>
      <DateRange
        size='sm'
        classNameButton='w-52'
        dateFormat='LLL. d'
        onCommit={(range) => {
          setFilters({ date_range: range ?? null });
        }}
        value={filters.date_range ?? undefined}
      />
      <Popover
        trigger={
          <Button variant='input' size='sm'>
            Phone
            {filters.from_number && (
              <>
                <div className='h-1/2 w-px bg-border' />
                <span className='text-muted-foreground'>{filters.from_number}</span>
              </>
            )}
            <ChevronDownIcon className='text-muted-foreground' />
          </Button>
        }
        onOpenChange={(v) => {
          if (!v) {
            setFilters({ from_number: phoneValue });
          }
        }}
        className='p-0.5'
      >
        <Input value={phoneValue} onChange={(e) => setPhoneValue(e.target.value)} />
      </Popover>
      <MultiSelect
        label='Category'
        selected={filters.intents}
        onChange={(values) => setFilters({ intents: values })}
        options={CALL_INTENTS}
      />
      {isOutcomeEnabled && (
        <MultiSelect
          label='Outcome'
          selected={filters.outcomes}
          onChange={(values) => setFilters({ outcomes: values })}
          options={CALL_OUTCOMES}
        />
      )}
      <MultiSelect
        label='Human Requested'
        selected={filters.human_requested}
        onChange={(values) => setFilters({ human_requested: values })}
        options={CALL_HUMAN_REQUEST}
      />
    </div>
  );
}
